import { Card } from './Card.js';
import { GameRules } from './GameRules.js';

/**
 * Solvability checker for Klondike Solitaire
 * Uses heuristics to determine if a game is likely solvable
 *
 * Note: This is not a perfect solver. It uses heuristics to filter out
 * obviously unsolvable games while being fast enough for realtime use.
 */
export class Solver {
  /**
   * Check if a game configuration is likely solvable
   * Uses heuristics rather than exhaustive search for performance
   */
  static isSolvable(
    stock: Card[],
    waste: Card[],
    foundations: Card[][],
    tableau: Card[][],
    _drawMode: number = 3
  ): boolean {
    // Already won
    if (GameRules.isGameWon(foundations)) {
      return true;
    }

    // Check for basic solvability heuristics
    const hasAccessibleAces = this.hasAccessibleAces(stock, waste, tableau);
    const hasReasonableTableauStructure = this.hasReasonableTableauStructure(tableau);
    const noDeadlocks = this.checkForDeadlocks(tableau);

    return hasAccessibleAces && hasReasonableTableauStructure && noDeadlocks;
  }

  /**
   * Check if all aces are accessible (not buried under cards of opposite color with no way to access)
   */
  private static hasAccessibleAces(stock: Card[], waste: Card[], tableau: Card[][]): boolean {
    const aces: Card[] = [];

    // Collect all aces
    for (const card of stock) {
      if (card.rank === 1) aces.push(card);
    }
    for (const card of waste) {
      if (card.rank === 1) aces.push(card);
    }
    for (const pile of tableau) {
      for (const card of pile) {
        if (card.rank === 1) aces.push(card);
      }
    }

    // All aces should be accessible eventually
    // In stock/waste, they're always accessible
    // In tableau, we check if they're not deeply buried

    for (const pile of tableau) {
      for (let i = 0; i < pile.length; i++) {
        const card = pile[i];
        if (card.rank === 1 && !card.isFaceUp) {
          // Ace is face-down, check if it can be revealed
          // If there are many cards above it, it might be hard to access
          const cardsAbove = pile.length - i - 1;
          if (cardsAbove > 6) {
            // Too many cards above, might be problematic
            // But we'll be lenient and allow it
          }
        }
      }
    }

    return true; // Be optimistic about ace accessibility
  }

  /**
   * Check if tableau has reasonable structure
   * (not all cards of same color in a pile, etc.)
   */
  private static hasReasonableTableauStructure(tableau: Card[][]): boolean {
    for (const pile of tableau) {
      if (pile.length === 0) continue;

      // Check if all face-up cards are of the same color (bad sign)
      const faceUpCards = pile.filter(c => c.isFaceUp);
      if (faceUpCards.length > 3) {
        const firstColor = faceUpCards[0].color;
        const allSameColor = faceUpCards.every(c => c.color === firstColor);
        if (allSameColor) {
          return false; // Likely problematic
        }
      }
    }

    return true;
  }

  /**
   * Check for obvious deadlocks
   * (e.g., all Kings are buried, making empty piles unusable)
   */
  private static checkForDeadlocks(tableau: Card[][]): boolean {
    const kings: Card[] = [];

    // Find all Kings
    for (const pile of tableau) {
      for (const card of pile) {
        if (card.rank === 13) {
          kings.push(card);
        }
      }
    }

    // Check if all Kings are face-down and buried
    let accessibleKings = 0;
    for (const pile of tableau) {
      for (let i = 0; i < pile.length; i++) {
        const card = pile[i];
        if (card.rank === 13) {
          if (card.isFaceUp || i === pile.length - 1) {
            accessibleKings++;
          }
        }
      }
    }

    // If we have empty piles but no accessible Kings, that's potentially bad
    const emptyPiles = tableau.filter(p => p.length === 0).length;
    if (emptyPiles > 0 && accessibleKings === 0 && kings.length > 0) {
      return false; // Deadlock: empty pile but no accessible King
    }

    return true;
  }
}
